import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useApiService } from '../../hooks/useApiService';
// Import Pell
import pell from 'pell';
import 'pell/dist/pell.min.css';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
  borderRadius: theme.spacing(2),
  minWidth: 150,
}));

const FloatingButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));

const DescriptionContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
}));

// Claude-like loading animation
const pulse = keyframes`
  0% {
    transform: scale(0.95);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.7;
  }
`;

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  zIndex: 2,
  borderRadius: theme.shape.borderRadius,
}));

const AnimatedCircle = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  animation: `${pulse} 1.5s infinite ease-in-out`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.primary.contrastText,
}));

// Pell Editor Wrapper
const EditorWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  '.pell': {
    borderRadius: theme.spacing(1),
  },
  '.pell-content': {
    minHeight: '250px',
    backgroundColor: '#ffffff',
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  '.pell-actionbar': {
    backgroundColor: '#f8f9fa',
    border: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '.pell-button': {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    height: '30px',
    width: '30px',
    margin: '0 2px',
    borderRadius: '3px',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '.pell-button-selected': {
    backgroundColor: theme.palette.action.selected,
  }
}));

// Location data structure
const locationData = {
  "Dhaka": {
    "Dhaka": ["Dhanmondi", "Mirpur", "Mohammadpur", "Gulshan", "Banani", "Uttara", "Demra", "Dohar", "Keraniganj", "Nawabganj", "Savar"],
    "Faridpur": ["Faridpur Sadar", "Alfadanga", "Bhanga", "Boalmari", "Charbhadrasan", "Madhukhali", "Nagarkanda", "Sadarpur", "Saltha"],
    "Gazipur": ["Gazipur Sadar", "Kaliakair", "Kaliganj", "Kapasia", "Sreepur"],
    "Gopalganj": ["Gopalganj Sadar", "Kashiani", "Kotalipara", "Muksudpur", "Tungipara"],
    "Kishoreganj": ["Kishoreganj Sadar", "Austagram", "Bajitpur", "Bhairab", "Hossainpur", "Itna", "Karimganj", "Katiadi", "Kuliarchar", "Mithamain", "Nikli", "Pakundia", "Tarail"],
    "Madaripur": ["Madaripur Sadar", "Kalkini", "Rajoir", "Shibchar"],
    "Manikganj": ["Manikganj Sadar", "Daulatpur", "Ghior", "Harirampur", "Saturia", "Shibalaya", "Singair"],
    "Munshiganj": ["Munshiganj Sadar", "Gazaria", "Lohajang", "Sirajdikhan", "Sreenagar", "Tongibari"],
    "Narayanganj": ["Narayanganj Sadar", "Araihazar", "Bandar", "Rupganj", "Sonargaon"],
    "Narsingdi": ["Narsingdi Sadar", "Belabo", "Monohardi", "Palash", "Raipura", "Shibpur"],
    "Rajbari": ["Rajbari Sadar", "Baliakandi", "Goalandaghat", "Pangsha"],
    "Shariatpur": ["Shariatpur Sadar", "Bhedarganj", "Damudya", "Gosairhat", "Naria", "Zajira"],
    "Tangail": ["Tangail Sadar", "Basail", "Bhuapur", "Delduar", "Dhanbari", "Ghatail", "Gopalpur", "Kalihati", "Madhupur", "Mirzapur", "Nagarpur", "Sakhipur"]
  },
  "Chittagong": {
    "Chittagong": ["Anwara", "Banshkhali", "Boalkhali", "Chandanaish", "Fatikchhari", "Hathazari", "Lohagara", "Mirsharai", "Patiya", "Rangunia", "Raozan", "Sandwip", "Satkania", "Sitakunda"],
    "Bandarban": ["Bandarban Sadar", "Thanchi", "Lama", "Naikhongchhari", "Ali Kadam", "Rowangchhari", "Ruma"],
    "Brahmanbaria": ["Brahmanbaria Sadar", "Ashuganj", "Nasirnagar", "Sarail", "Kasba", "Akhaura", "Bancharampur", "Bijoynagar"],
    "Chandpur": ["Chandpur Sadar", "Faridganj", "Haimchar", "Haziganj", "Kachua", "Matlab Dakshin", "Matlab Uttar", "Shahrasti"],
    "Comilla": ["Comilla Sadar", "Barura", "Brahmanpara", "Burichang", "Chandina", "Chauddagram", "Daudkandi", "Debidwar", "Homna", "Laksam", "Monohorgonj", "Meghna", "Muradnagar", "Nangalkot", "Titas"],
    "Cox's Bazar": ["Cox's Bazar Sadar", "Chakaria", "Kutubdia", "Maheshkhali", "Ramu", "Teknaf", "Ukhia"],
    "Feni": ["Feni Sadar", "Chhagalnaiya", "Daganbhuiyan", "Parshuram", "Fulgazi", "Sonagazi"],
    "Khagrachhari": ["Khagrachhari Sadar", "Dighinala", "Panchhari", "Lakshmichhari", "Mahalchhari", "Manikchhari", "Matiranga", "Ramgarh"],
    "Lakshmipur": ["Lakshmipur Sadar", "Raipur", "Ramganj", "Ramgati", "Kamalnagar"],
    "Noakhali": ["Noakhali Sadar", "Begumganj", "Chatkhil", "Companiganj", "Hatiya", "Senbagh", "Sonaimuri", "Subarnachar", "Kabirhat"],
    "Rangamati": ["Rangamati Sadar", "Bagaichhari", "Barkal", "Juraichhari", "Rajasthali", "Kaptai", "Langadu", "Naniarchar", "Kaukhali", "Belaichhari"]
  },
  "Sylhet": {
    "Sylhet": ["Sylhet Sadar", "Beanibazar", "Bishwanath", "Companiganj", "Fenchuganj", "Golapganj", "Gowainghat", "Jaintiapur", "Kanaighat", "Osmani Nagar", "Zakiganj"],
    "Habiganj": ["Habiganj Sadar", "Ajmiriganj", "Baniachang", "Bahubal", "Chunarughat", "Lakhai", "Madhabpur", "Nabiganj", "Shaistaganj"],
    "Moulvibazar": ["Moulvibazar Sadar", "Kamalganj", "Kulaura", "Rajnagar", "Sreemangal", "Barlekha", "Juri"],
    "Sunamganj": ["Sunamganj Sadar", "Bishwamvarpur", "Chhatak", "Derai", "Dharampasha", "Dowarabazar", "Jagannathpur", "Jamalganj", "Sullah", "Tahirpur", "Dakshin Sunamganj"]
  },
  "Rajshahi": {
    "Rajshahi": ["Rajshahi Sadar", "Bagha", "Bagmara", "Charghat", "Durgapur", "Godagari", "Mohanpur", "Paba", "Puthia", "Tanore"],
    "Bogra": ["Bogra Sadar", "Adamdighi", "Dhunat", "Dupchanchia", "Gabtali", "Kahaloo", "Nandigram", "Sariakandi", "Sherpur", "Shibganj", "Sonatala"],
    "Chapai Nawabganj": ["Chapai Nawabganj Sadar", "Bholahat", "Gomastapur", "Nachole", "Shibganj"],
    "Joypurhat": ["Joypurhat Sadar", "Akkelpur", "Kalai", "Khetlal", "Panchbibi"],
    "Naogaon": ["Naogaon Sadar", "Atrai", "Badalgachi", "Dhamoirhat", "Manda", "Mahadevpur", "Niamatpur", "Patnitala", "Porsha", "Raninagar", "Sapahar"],
    "Natore": ["Natore Sadar", "Bagatipara", "Baraigram", "Gurudaspur", "Lalpur", "Singra"],
    "Pabna": ["Pabna Sadar", "Atgharia", "Bera", "Bhangura", "Chatmohar", "Faridpur", "Ishwardi", "Santhia", "Sujanagar"],
    "Sirajganj": ["Sirajganj Sadar", "Belkuchi", "Chauhali", "Kamarkhanda", "Kazipur", "Raiganj", "Shahjadpur", "Tarash", "Ullahpara"]
  },
  "Rangpur": {
    "Rangpur": ["Rangpur Sadar", "Badarganj", "Gangachara", "Kaunia", "Mithapukur", "Pirgachha", "Pirganj", "Taraganj"],
    "Dinajpur": ["Dinajpur Sadar", "Birampur", "Birganj", "Biral", "Bochaganj", "Chirirbandar", "Fulbari", "Ghoraghat", "Hakimpur", "Kaharole", "Khansama", "Nawabganj", "Parbatipur"],
    "Gaibandha": ["Gaibandha Sadar", "Fulchhari", "Gobindaganj", "Palashbari", "Sadullapur", "Sundarganj", "Saghata"],
    "Kurigram": ["Kurigram Sadar", "Bhurungamari", "Char Rajibpur", "Chilmari", "Phulbari", "Rajarhat", "Raomari", "Ulipur"],
    "Lalmonirhat": ["Lalmonirhat Sadar", "Aditmari", "Kaliganj", "Hatibandha", "Patgram", "Dimla"],
    "Nilphamari": ["Nilphamari Sadar", "Dimla", "Domar", "Jaldhaka", "Kishoreganj", "Saidpur"],
    "Panchagarh": ["Panchagarh Sadar", "Atwari", "Boda", "Debiganj", "Tetulia"],
    "Thakurgaon": ["Thakurgaon Sadar", "Baliadangi", "Haripur", "Pirganj", "Ranisankail"]
  },
  "Khulna": {
    "Khulna": ["Khulna Sadar", "Batiaghata", "Dacope", "Dumuria", "Dighalia", "Koyra", "Paikgachha", "Phultala", "Rupsa", "Terokhada"],
    "Bagerhat": ["Bagerhat Sadar", "Chitalmari", "Fakirhat", "Kachua", "Mollahat", "Mongla", "Morrelganj", "Rampal", "Sarankhola"],
    "Chuadanga": ["Chuadanga Sadar", "Alamdanga", "Damurhuda", "Jibannagar"],
    "Jessore": ["Jessore Sadar", "Abhaynagar", "Bagherpara", "Chaugachha", "Jhikargachha", "Keshabpur", "Manirampur", "Sharsha"],
    "Jhenaidah": ["Jhenaidah Sadar", "Harinakunda", "Kaliganj", "Kotchandpur", "Maheshpur", "Shailkupa"],
    "Kushtia": ["Kushtia Sadar", "Bheramara", "Daulatpur", "Khoksa", "Kumarkhali", "Mirpur"],
    "Magura": ["Magura Sadar", "Mohammadpur", "Shalikha", "Sreepur"],
    "Meherpur": ["Meherpur Sadar", "Gangni", "Mujibnagar"],
    "Narail": ["Narail Sadar", "Kalia", "Lohagara"],
    "Satkhira": ["Satkhira Sadar", "Assasuni", "Debhata", "Kalaroa", "Kaliganj", "Shyamnagar", "Tala"]
  },
  "Barishal": {
    "Barishal": ["Barishal Sadar", "Agailjhara", "Babuganj", "Bakerganj", "Banaripara", "Gaurnadi", "Hizla", "Mehendiganj", "Muladi", "Wazirpur"],
    "Barguna": ["Barguna Sadar", "Amtali", "Bamna", "Betagi", "Patharghata", "Taltali"],
    "Bhola": ["Bhola Sadar", "Burhanuddin", "Char Fasson", "Daulatkhan", "Lalmohan", "Manpura", "Tazumuddin"],
    "Jhalokati": ["Jhalokati Sadar", "Kathalia", "Nalchity", "Rajapur"],
    "Patuakhali": ["Patuakhali Sadar", "Bauphal", "Dashmina", "Galachipa", "Kalapara", "Mirzaganj", "Rangabali"],
    "Pirojpur": ["Pirojpur Sadar", "Bhandaria", "Kawkhali", "Mathbaria", "Nazirpur", "Nesarabad", "Zianagar"]
  },
  "Mymensingh": {
    "Mymensingh": ["Mymensingh Sadar", "Bhaluka", "Dhobaura", "Fulbaria", "Gaffargaon", "Gauripur", "Haluaghat", "Ishwarganj", "Muktagachha", "Nandail", "Phulpur", "Trishal"],
    "Jamalpur": ["Jamalpur Sadar", "Bakshiganj", "Dewanganj", "Islampur", "Madarganj", "Melandaha", "Sarishabari"],
    "Netrokona": ["Netrokona Sadar", "Atpara", "Barhatta", "Durgapur", "Khaliajuri", "Kendua", "Madan", "Mohanganj", "Purbadhala"],
    "Sherpur": ["Sherpur Sadar", "Jhenaigati", "Nakla", "Nalitabari", "Sreebardi"]
  }
};

const AddBazar = () => {
  const { useAi } = useApiService();
  const { generateContent, isGenerating } = useAi();

  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState("");
  const [bazarName, setBazarName] = useState("");
  const [division, setDivision] = useState("");
  const [district, setDistrict] = useState("");
  const [upazilla, setUpazilla] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const editorRef = useRef(null);
  const pellInstance = useRef(null);
  const editorInitialized = useRef(false);

  const handleDivisionChange = (event) => {
    const selectedDivision = event.target.value;
    setDivision(selectedDivision);
    setDistrict(""); // Reset district when division changes
    setUpazilla(""); // Reset upazilla when division changes
  };

  const handleDistrictChange = (event) => {
    const selectedDistrict = event.target.value;
    setDistrict(selectedDistrict);
    setUpazilla(""); // Reset upazilla when district changes
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const typewriterEffect = (text, speed = 10) => {
    setIsTyping(true);
    setIsLoading(false); // Hide loading animation when typewriter starts
    
    let i = 0;
    let displayText = '';
    setDescription(''); // Clear existing content
    
    // Make typing much faster - target 1-2 seconds total
    const textLength = text.length;
    const targetDuration = 1500; // 1.5 seconds in milliseconds
    
    // Use character batching for faster animation
    // Calculate how many characters to add each interval
    const totalIntervals = 20; // Use only 20 steps for the entire animation
    const charsPerInterval = Math.max(1, Math.ceil(textLength / totalIntervals));
    const intervalTime = Math.floor(targetDuration / totalIntervals);
    
    const typing = setInterval(() => {
      if (i < textLength) {
        // Add multiple characters at once
        const endIndex = Math.min(i + charsPerInterval, textLength);
        displayText += text.substring(i, endIndex);
        setDescription(displayText);
        i = endIndex;
      } else {
        clearInterval(typing);
        setIsTyping(false);
        setSuccessMessage("Description generated successfully!");
      }
    }, intervalTime);

    // Cleanup function
    return () => clearInterval(typing);
  };

  // Handle description change
  const handleDescriptionChange = (html) => {
    if (!isTyping) {
      setDescription(html);
    }
  };

  // Initialize Pell editor only once when component mounts
  useEffect(() => {
    // Only initialize if the ref exists and editor not already initialized
    if (editorRef.current && !editorInitialized.current) {
      // Clean up any existing editor first
      if (editorRef.current.querySelector('.pell')) {
        editorRef.current.innerHTML = '';
      }
      
      pellInstance.current = pell.init({
        element: editorRef.current,
        onChange: handleDescriptionChange,
        defaultParagraphSeparator: 'p',
        styleWithCSS: false,
        actions: [
          'bold',
          'italic',
          'underline',
          'strikethrough',
          'heading1',
          'heading2',
          'paragraph',
          'quote',
          'olist',
          'ulist',
          'link',
          'image',
          'line'
        ],
      });

      // Set initial content if needed
      if (description) {
        pellInstance.current.content.innerHTML = description;
      }
      
      editorInitialized.current = true;
    }

    // Clean up function
    return () => {
      if (pellInstance.current) {
        // Clean up the editor instance
        if (editorRef.current) {
          editorRef.current.innerHTML = '';
        }
        pellInstance.current = null;
      }
      editorInitialized.current = false;
    };
  }, [editorRef]); // Only re-run if editorRef changes

  // Update Pell content when description changes from external sources (like AI generation)
  useEffect(() => {
    if (pellInstance.current && pellInstance.current.content && isTyping) {
      pellInstance.current.content.innerHTML = description;
    }
  }, [description, isTyping]);

  const generateDescription = async () => {
    if (!language) {
      setErrorMessage("Please select a language first!");
      return;
    }

    if (!bazarName) {
      setErrorMessage("Please enter a bazar name!");
      return;
    }

    // Close the dialog immediately when starting to generate
    handleCloseDialog();
    
    // Show loading animation
    setIsLoading(true);
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const prompt = language === "Bangla" 
        ? `"${bazarName}" নামক বাজারের একটি বর্ণনা তৈরি করুন ৩০০ শব্দের মধ্যে। অবস্থান: ${upazilla}, ${district}, ${division} বিভাগ। দোকানের ধরন, বিশেষ বৈশিষ্ট্য এবং স্থানীয় গুরুত্ব সম্পর্কে লিখুন। সরাসরি বর্ণনা দিয়ে শুরু করুন, কোনো ভূমিকা বা ব্যাখ্যা ছাড়াই।`
        : `Generate a 300-word description of "${bazarName}" marketplace located in ${upazilla}, ${district}, ${division} Division. Focus on shop types, special features, and local significance. Start directly with the description without any introduction or explanation.`;

      // Use the API service hook instead of direct axios call
      const generatedText = await generateContent(prompt);
      
      // Use typewriter effect to display the text
      typewriterEffect(generatedText);
    } catch (error) {
      console.error("Error details:", error);
      setIsLoading(false); // Hide loading on error
      
      let errorMessage = "Failed to generate description. Please try again.";
      
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        errorMessage = "No response from server. Please check your internet connection.";
      }
      
      setErrorMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setBazarName("");
    setDescription("");
    setLanguage("");
    setDivision("");
    setDistrict("");
    setUpazilla("");
    setErrorMessage("");
    setSuccessMessage("");
    
    // Also clear the editor content if it exists
    if (pellInstance.current && pellInstance.current.content) {
      pellInstance.current.content.innerHTML = '';
    }
  };

  const handleSave = () => {
    // Implement save functionality
    console.log("Saving bazar...");
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <StyledPaper>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Division</InputLabel>
              <Select
                value={division}
                onChange={handleDivisionChange}
                label="Division"
              >
                <MenuItem value="">
                  <em>Select Division</em>
                </MenuItem>
                {Object.keys(locationData).map((div) => (
                  <MenuItem key={div} value={div}>
                    {div}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>District</InputLabel>
              <Select
                value={district}
                onChange={handleDistrictChange}
                label="District"
                disabled={!division}
              >
                <MenuItem value="">
                  <em>Select District</em>
                </MenuItem>
                {division && Object.keys(locationData[division]).map((dist) => (
                  <MenuItem key={dist} value={dist}>
                    {dist}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Upazilla</InputLabel>
              <Select
                value={upazilla}
                onChange={(e) => setUpazilla(e.target.value)}
                label="Upazilla"
                disabled={!district}
              >
                <MenuItem value="">
                  <em>Select Upazilla</em>
                </MenuItem>
                {district && locationData[division][district].map((upz) => (
                  <MenuItem key={upz} value={upz}>
                    {upz}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TextField
            fullWidth
            label="Contact Number"
            variant="outlined"
          />

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Bazar Details
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Box>

          <TextField
            fullWidth
            label="Bazar Name"
            value={bazarName}
            onChange={(e) => setBazarName(e.target.value)}
            variant="outlined"
            sx={{ mb: 3 }}
          />

          <Typography variant="h6" gutterBottom>
            Bazar Description
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <DescriptionContainer>
            <EditorWrapper>
              <div
                ref={editorRef}
                className="pell-container"
              ></div>
              <input
                type="hidden"
                name="description"
                value={description}
              />
            </EditorWrapper>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleOpenDialog}
                startIcon={<AutoAwesomeIcon />}
                disabled={isGenerating || isTyping}
              >
                Generate with AI
              </Button>
            </Box>
          </DescriptionContainer>

          {errorMessage && (
            <Alert severity="error">
              {errorMessage}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success">
              {successMessage}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <StyledButton
              variant="outlined"
              color="secondary"
              onClick={handleClear}
            >
              Clear
            </StyledButton>
            <StyledButton
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!bazarName || !division || !district || !upazilla}
            >
              Save Bazar
            </StyledButton>
          </Box>
        </Box>
      </StyledPaper>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Select Language for Description</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Language</InputLabel>
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              label="Language"
            >
              <MenuItem value="">
                <em>Select Language</em>
              </MenuItem>
              <MenuItem value="English">English</MenuItem>
              <MenuItem value="Bangla">Bangla</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={generateDescription} 
            variant="contained" 
            disabled={!language || loading}
          >
            {loading ? "Generating..." : "Generate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AddBazar;